import { ActionEvents } from '@griply/types';
import { addTaskToSection, taskTypeToTrackingTaskType } from '@griply/utils';
import { useUpdateGoal } from 'features/goals';
import { useCallback, useRef } from 'react';
import { useCreateTaskWithSubTasksMutation } from 'shared/hooks/queries/use-create-task-with-sub-tasks-mutation';
import { useRelationships } from 'shared/hooks/use-relationships';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { TaskRelationshipLink } from 'shared/services/backend-api';
import { Goal } from 'shared/types/goal';
import { ID, IDPrefixes } from 'shared/types/id';
import {
  goalInboxID,
  goalOtherID,
  lifeAreaOtherID,
} from 'shared/types/orderable-section';
import { NewSubTask, NewTask, Task } from 'shared/types/task';
import { TaskActionType } from 'shared/types/task-action';
import { createPrefixId } from 'shared/utils/prefix-id';

const excludedGoalIds = [goalInboxID, goalOtherID];

export type ExtraOptions = {
  sectionId?: string;
  goal?: Goal;
  tags?: ID[] | null;
  subTasks?: NewSubTask[];
};

export const useCreateTask = () => {
  const track = useTrackEvents();
  const createTaskRef = useRef<NewTask>();
  const subTasksRef = useRef<NewSubTask[]>([]);
  const {
    mutateAsync: createTaskWithSubTasks,
    error,
    isPending,
    isSuccess,
    reset,
  } = useCreateTaskWithSubTasksMutation();
  // todo: find a better solution for this (cannot use other features here)
  const { submit: updateGoal } = useUpdateGoal();
  const relationships = useRelationships();

  const submit = useCallback(
    async (
      task: NewTask,
      { goal, sectionId, tags, subTasks = [] }: ExtraOptions = {},
    ): Promise<Task> => {
      // filter out inbox ID
      task.goalId = excludedGoalIds.includes(task.goalId!) ? null : task.goalId;
      task.lifeAreaId =
        task.lifeAreaId === lifeAreaOtherID ? null : task.lifeAreaId;

      // Link tags in the same write batch as the task so the first snapshot
      // already shows it tagged — otherwise the list animates it from the
      // untagged bucket into its tag group.
      const relationshipLinks: TaskRelationshipLink[] = (tags ?? []).map(
        (tagId) => {
          const id = createPrefixId(tagId, IDPrefixes.Tag);
          const existing = relationships.find(
            (relationship) => relationship.id === id,
          );
          return { id, existingReferences: existing?.references ?? null };
        },
      );

      createTaskRef.current = task;
      subTasksRef.current = subTasks;
      const { id } = await createTaskWithSubTasks({
        task,
        subTasks,
        relationshipLinks,
      });

      // update the goal if the task is bound to a goal and not an actionable task
      if (task.goalId && task.action?.type !== TaskActionType.GoalTimeslot) {
        // if a section is provided, add the task to it
        const sections =
          sectionId && goal ? addTaskToSection(goal, sectionId, id) : undefined;

        updateGoal({ id: task.goalId, taskCount: 1, sections });
      }

      track(ActionEvents.TaskCreate, {
        type: taskTypeToTrackingTaskType[task.type],
        deadline_added: !!task.endStrategy?.deadline,
        linked_goal: !!task.goalId,
      });

      return {
        ...task,
        id,
      } as Task;
    },
    [createTaskWithSubTasks, track, updateGoal, relationships],
  );

  const retry = useCallback(async () => {
    const task = createTaskRef.current;
    const subTasks = subTasksRef.current;

    if (task) {
      const { id } = await createTaskWithSubTasks({ task, subTasks });

      track(ActionEvents.TaskCreate, {
        type: taskTypeToTrackingTaskType[task.type],
        deadline_added: !!task.endStrategy?.deadline,
        linked_goal: !!task.goalId,
      });

      return {
        ...task,
        id,
      } as Task;
    }
  }, [createTaskWithSubTasks, track]);

  const resetAll = useCallback(() => {
    reset();
    createTaskRef.current = undefined;
  }, [reset]);

  return {
    submit,
    retry,
    reset: resetAll,
    error,
    isLoading: isPending,
    isSuccess,
  };
};
